import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { formatCurrency } from '../lib/format'
import type { Reminder } from '../lib/types'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/shadcn/dialog'
import { track } from '../lib/analytics'

export interface PaletteItem {
  id: string
  label: string
  // Shown muted after the label, e.g. the sidebar group a page lives in.
  hint?: string
  // Extra words the label alone would not match ("billing" -> Settings).
  keywords?: string
  section: 'Pages' | 'Actions' | 'Settings' | 'Help' | 'Reminders'
  run: () => void
}

const MAX_REMINDER_RESULTS = 8

// Every query word must appear in the item's text; words that start the
// label or a word in it rank higher. Small on purpose -- the list is a few
// dozen pages plus the org's reminders, so no fuzzy-matching library.
function score(item: { label: string; hint?: string; keywords?: string }, words: string[]): number {
  if (words.length === 0) return 1
  const label = item.label.toLowerCase()
  const haystack = `${label} ${item.hint ?? ''} ${item.keywords ?? ''}`.toLowerCase()
  let total = 0
  for (const word of words) {
    if (!haystack.includes(word)) return 0
    if (label.startsWith(word)) total += 3
    else if (label.includes(` ${word}`)) total += 2
    else total += 1
  }
  return total
}

/**
 * Cmd/Ctrl+K menu: jump to a page, run a quick action, or find a reminder.
 * Pages and actions come from the caller (so it always matches what the
 * sidebar shows this user); reminders reuse the ['reminders'] query cache
 * and are matched in the browser, org-scoped by the API like everywhere else.
 */
export function CommandPalette({
  open,
  onOpenChange,
  items,
  currency,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  items: PaletteItem[]
  currency: string
}) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey) && !event.altKey) {
        // Replaces the browser's own Ctrl+K (focus the address bar's search).
        event.preventDefault()
        onOpenChange(!open)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onOpenChange])

  useEffect(() => {
    if (open) {
      setQuery('')
      setActive(0)
    }
  }, [open])

  const { data: reminders } = useQuery({
    queryKey: ['reminders'],
    queryFn: () => api.get<Reminder[]>('/api/v1/reminders'),
    enabled: open,
  })

  const results = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
    const ranked = items
      .map((item) => ({ item, s: score(item, words) }))
      .filter((entry) => entry.s > 0)
      .sort((a, b) => b.s - a.s)
      .map((entry) => entry.item)

    // Reminders only once something is typed -- listing the first few of
    // hundreds on an empty box is noise.
    const found: PaletteItem[] =
      words.length === 0
        ? []
        : (reminders ?? [])
            .map((r) => {
              const label = r.client_name ? `${r.title} · ${r.client_name}` : r.title
              const hint = [formatCurrency(r.amount, currency), r.status].filter(Boolean).join(' · ')
              return {
                s: score({ label, keywords: `${r.client_id} ${r.client_name ?? ''}` }, words),
                item: {
                  id: `reminder-${r.reminder_id}`,
                  label,
                  hint,
                  section: 'Reminders' as const,
                  run: () => navigate(`/reminders?open=${r.reminder_id}`),
                },
              }
            })
            .filter((entry) => entry.s > 0)
            .sort((a, b) => b.s - a.s)
            .slice(0, MAX_REMINDER_RESULTS)
            .map((entry) => entry.item)

    return [...ranked, ...found]
  }, [items, reminders, query, currency, navigate])

  // Typing shrinks the list; keep the highlight on a row that still exists.
  const activeIndex = Math.min(active, Math.max(results.length - 1, 0))

  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, results])

  const choose = (item: PaletteItem | undefined) => {
    if (!item) return
    if (query.trim()) track('search_used')
    onOpenChange(false)
    item.run()
  }

  const onInputKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive(results.length ? (activeIndex + 1) % results.length : 0)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive(results.length ? (activeIndex - 1 + results.length) % results.length : 0)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      choose(results[activeIndex])
    }
  }

  let lastSection = ''

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="top-[15vh] flex max-h-[70vh] translate-y-0 flex-col gap-0 p-0 sm:max-w-xl"
        data-testid="command-palette"
      >
        <DialogTitle className="sr-only">Search and jump to</DialogTitle>
        <DialogDescription className="sr-only">
          Type to find a page, an action or a reminder. Use the arrow keys and Enter.
        </DialogDescription>
        <input
          autoFocus
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setActive(0)
          }}
          onKeyDown={onInputKeyDown}
          placeholder="Search pages, actions and reminders…"
          role="combobox"
          aria-expanded="true"
          aria-controls="command-palette-list"
          aria-activedescendant={results[activeIndex] ? `palette-${results[activeIndex].id}` : undefined}
          aria-label="Search"
          className="w-full border-b border-border bg-transparent px-4 py-3 text-sm text-foreground outline-none placeholder:text-muted-foreground"
        />
        <div ref={listRef} id="command-palette-list" role="listbox" className="overflow-y-auto p-2">
          {results.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">Nothing matches “{query}”.</p>
          )}
          {results.map((item, index) => {
            const showHeading = item.section !== lastSection
            lastSection = item.section
            return (
              <div key={item.id}>
                {showHeading && (
                  <div className="px-3 pb-1 pt-2 text-xs font-medium text-muted-foreground">{item.section}</div>
                )}
                <div
                  id={`palette-${item.id}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  onMouseMove={() => setActive(index)}
                  onClick={() => choose(item)}
                  className={`flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm ${
                    index === activeIndex ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'text-foreground'
                  }`}
                >
                  <span className="truncate">{item.label}</span>
                  {item.hint && <span className="ml-auto shrink-0 text-xs text-muted-foreground">{item.hint}</span>}
                </div>
              </div>
            )
          })}
        </div>
        <div className="hidden gap-4 border-t border-border px-4 py-2 text-xs text-muted-foreground sm:flex">
          <span>↑↓ move</span>
          <span>Enter open</span>
          <span>Esc close</span>
        </div>
      </DialogContent>
    </Dialog>
  )
}
